import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import {
  AppButton,
  AppForm,
  AppFormLabel,
  AppRadio,
  AppRadioGroup,
  AppSelect,
} from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import useConsentTreatmentPeriodMassagers from '@/hooks/main/useConsentTreatmentPeriodMassagers';
import {
  consentTreatmentPeriodSearchSchema,
  type ConsentTreatmentPeriodSearchFormData,
} from '@/schemas/consentTreatmentPeriodSchema';
import { dateValueToIso } from '@/utils/dateConverter';

export interface ConsentTreatmentPeriodSearchFormProps {
  /** "はり・きゅう師" (A2) or "マッサージ師" (R2) — supplied by the page. */
  massagerLabel: string;
  onSearch: (values: ConsentTreatmentPeriodSearchFormData) => void;
  /**
   * Surface validation errors as toastr (matches TreatmentRecordListPage
   * pattern — no inline help text under the inputs). The first failing
   * rule's message is passed, with 施術師 already replaced by massagerLabel.
   */
  onValidationError: (message: string) => void;
  defaultValues?: Partial<ConsentTreatmentPeriodSearchFormData>;
  /** Pass useQuery.isFetching here so the 検索 button shows spinner while loading. */
  isLoading?: boolean;
}

const DEFAULTS: ConsentTreatmentPeriodSearchFormData = {
  search_treatement_from: '',
  search_treatement_to: '',
  massager: '1',
  massager_id: null,
};

const ConsentTreatmentPeriodSearchForm: React.FC<ConsentTreatmentPeriodSearchFormProps> = ({
  massagerLabel,
  onSearch,
  onValidationError,
  defaultValues,
  isLoading = false,
}) => {
  const { control, handleSubmit, watch, setValue, getValues } =
    useTrimmedForm<ConsentTreatmentPeriodSearchFormData>({
      resolver: zodResolver(consentTreatmentPeriodSearchSchema),
      defaultValues: { ...DEFAULTS, ...defaultValues },
    });

  const from = watch('search_treatement_from');
  const to = watch('search_treatement_to');
  const massagerMode = watch('massager');

  // RA2NDatePicker stores JP string (e.g. 2026年04月22日) — convert to ISO for API.
  const fromIso = dateValueToIso(from) ?? undefined;
  const toIso = dateValueToIso(to) ?? undefined;

  const { data: massagerData, refetch: refetchMassagers } = useConsentTreatmentPeriodMassagers(
    fromIso,
    toIso
  );
  const massagerOptions = useMemo(() => {
    const list = massagerData?.data?.massagers ?? [];
    return list.map(m => ({
      value: m.id,
      label: `${m.serial_number ?? ''}:${m.name ?? ''}`,
    }));
  }, [massagerData]);

  // When from/to change and current massager_id is no longer in options, clear it.
  // Use getValues (snapshot) — watch() inside effect creates an extra subscription
  // without benefit.
  useEffect(() => {
    const current = getValues('massager_id');
    if (current && !massagerOptions.some(o => o.value === current)) {
      setValue('massager_id', null, { shouldDirty: false });
    }
  }, [massagerOptions, setValue, getValues]);

  const massagerDisabled = massagerMode !== '2';

  // Surface the first failing rule via toastr (parent decides UX).
  // Order matches RA2 culum_control: from → to → massager_id.
  const handleValidationError = (errors: Record<string, { message?: string } | undefined>) => {
    const order: Array<keyof ConsentTreatmentPeriodSearchFormData> = [
      'search_treatement_from',
      'search_treatement_to',
      'massager_id',
    ];
    for (const key of order) {
      const msg = errors[key]?.message;
      if (msg) {
        onValidationError(msg.replace(/施術師/, massagerLabel));
        return;
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit(
        values =>
          onSearch({
            ...values,
            search_treatement_from: dateValueToIso(values.search_treatement_from) ?? '',
            search_treatement_to: dateValueToIso(values.search_treatement_to) ?? '',
          }),
        handleValidationError as any
      )}
      className="p-3 bg-white sm:p-4"
    >
      <div className="mb-3 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)] xl:items-start">
        <div className="flex flex-col min-w-0 gap-2 sm:flex-row sm:items-center">
          <AppFormLabel label="期間" className="shrink-0 sm:w-16" />
          <Controller
            control={control}
            name="search_treatement_from"
            render={({ field }) => (
              <RA2NDatePicker
                value={field.value}
                onChange={field.onChange}
                placeholder="元号00年00月00日"
                className="w-full"
              />
            )}
          />
          <span className="px-1 text-center sm:text-left">～</span>
          <Controller
            control={control}
            name="search_treatement_to"
            render={({ field }) => (
              <RA2NDatePicker
                value={field.value}
                onChange={field.onChange}
                placeholder="元号00年00月00日"
                className="w-full"
              />
            )}
          />
        </div>

        <div className="flex flex-col min-w-0 gap-3 lg:flex-row lg:items-center">
          <AppFormLabel label={massagerLabel} className="shrink-0" />
          <AppRadioGroup
            value={massagerMode}
            onChange={e => {
              const v = e.target.value as '1' | '2';
              setValue('massager', v);
              if (v === '1') {
                setValue('massager_id', null);
              } else {
                // Match RA2 blade behavior: switching to 指定あり re-fetches
                // the massager dropdown so options reflect the latest filter.
                refetchMassagers();
              }
            }}
            className="flex flex-wrap gap-x-6 gap-y-1"
          >
            <AppRadio value="1">指定なし</AppRadio>
            <AppRadio value="2">指定あり</AppRadio>
          </AppRadioGroup>
          <div className="w-full lg:w-[260px]">
            <Controller
              control={control}
              name="massager_id"
              render={({ field, fieldState }) => (
                <AppForm.Item validateStatus={fieldState.invalid ? 'error' : ''} className="mb-0">
                  <AppSelect
                    value={field.value ?? undefined}
                    onChange={(v: any) => field.onChange(v ?? null)}
                    onBlur={field.onBlur}
                    options={massagerOptions}
                    disabled={massagerDisabled}
                    placeholder="選択してください"
                    className="w-full"
                  />
                </AppForm.Item>
              )}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-center">
        <AppButton
          type="primary"
          htmlType="submit"
          loading={isLoading}
          className="w-full sm:w-auto"
        >
          <i className="mr-1 fa fa-search" /> 検索
        </AppButton>
      </div>
    </form>
  );
};

export default ConsentTreatmentPeriodSearchForm;
